import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { listEntries, fileUrl } from "../lib/api";
import { PROPERTIES } from "../lib/properties";

export default function Gallery() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const nestId = searchParams.get("nest") || "";

  const activeNest = useMemo(
    () => PROPERTIES.find((p) => p.id === nestId) || null,
    [nestId],
  );

  useEffect(() => {
    setLoading(true);
    listEntries(nestId || undefined)
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [nestId]);

  const setNest = (id) => {
    if (id) setSearchParams({ nest: id });
    else setSearchParams({});
  };

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 sm:pt-16 pb-20 sm:pb-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[#111111] font-semibold">
            The gallery
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#111] tracking-tighter mt-2">
            {activeNest ? `${activeNest.name} pages.` : "Every guest left a page."}
          </h1>
          {activeNest && (
            <p className="text-sm text-[#717171] mt-2">
              Filtered by {activeNest.name} · {activeNest.location}.{" "}
              <button
                data-testid="clear-nest-filter"
                onClick={() => setNest("")}
                className="underline underline-offset-2 text-[#111111] hover:text-[#c85040]"
              >
                Show all
              </button>
            </p>
          )}
        </div>
        <Link
          to={activeNest ? `/sign?nest=${activeNest.id}` : "/sign"}
          data-testid="gallery-sign-cta"
          className="gb-btn-primary inline-flex items-center gap-2 self-start"
        >
          Add your page <ArrowRight size={16} />
        </Link>
      </div>

      {/* Nest filter chips */}
      <div className="mt-6 flex flex-wrap gap-2">
        <button
          data-testid="nest-chip-all"
          onClick={() => setNest("")}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            !nestId
              ? "bg-[#222] text-white border-[#222]"
              : "bg-white text-[#111] border-[#EBEBEB] hover:border-[#cfcfcf]"
          }`}
        >
          All nests
        </button>
        {PROPERTIES.map((p) => (
          <button
            key={p.id}
            data-testid={`nest-chip-${p.id}`}
            onClick={() => setNest(p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              nestId === p.id
                ? "bg-[#111111] text-white border-[#111111]"
                : "bg-white text-[#111] border-[#EBEBEB] hover:border-[#cfcfcf]"
            }`}
          >
            {p.name}
          </button>
        ))}
      </div>

      {loading && (
        <p className="mt-12 text-[#717171]">Loading entries...</p>
      )}

      {!loading && entries.length === 0 && (
        <div className="mt-16 gb-card p-16 text-center">
          <h3 className="font-heading font-bold text-[#111] text-2xl tracking-tight">
            No entries yet.
          </h3>
          <p className="text-[#717171] mt-2">
            Be the first to leave a page in this gallery.
          </p>
          <Link
            to="/sign"
            data-testid="empty-sign-cta"
            className="gb-btn-primary inline-flex items-center gap-2 mt-6"
          >
            Sign the gallery <ArrowRight size={16} />
          </Link>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 justify-items-center">
          {entries.map((e, idx) => (
            <motion.div
              key={e.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05, duration: 0.4 }}
              className="w-full max-w-xs"
            >
              <Link
                to={`/gallery/${e.id}`}
                data-testid={`gallery-item-${e.id}`}
                className={`block polaroid ${idx % 2 === 0 ? "tilt-left" : "tilt-right"}`}
              >
                <div className="polaroid-tape" aria-hidden="true" />
                <div className="polaroid-photo">
                  <img
                    src={fileUrl(e.image_path)}
                    alt={`Entry by ${e.guest_name}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="polaroid-caption">
                  {e.guest_name}
                  <span className="sub">
                    {new Date(e.created_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </Link>
              {e.message && (
                <p className="text-sm text-[#454545] mt-6 italic text-center px-4 line-clamp-2">
                  &ldquo;{e.message}&rdquo;
                </p>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
}
